import { useMemo } from "react";
import {
  looksLikeRegex,
  parseSearchQuery,
  getValueByPath,
} from "../utils/searchUtils";
import { ALLOWED_MODELS, isModelAllowed } from "../constants/allowedModels";

// Information Extraction Leaderboard Scores
const LEADERBOARD_SCORES = {
  // 模型名称: { ner, finer_ord, finred, sc, cd, fnxl, fsrl }
  "GPT-4o": { ner: "To be added", finer_ord: "To be added", finred: "To be added", sc: "To be added", cd: "To be added", fnxl: "To be added", fsrl: "To be added" },
  "GPT-5": { ner: "To be added", finer_ord: "To be added", finred: "To be added", sc: "To be added", cd: "To be added", fnxl: "To be added", fsrl: "To be added" },
  "o3-mini": { ner: "To be added", finer_ord: "To be added", finred: "To be added", sc: "To be added", cd: "To be added", fnxl: "To be added", fsrl: "To be added" },
  "o3": { ner: "To be added", finer_ord: "To be added", finred: "To be added", sc: "To be added", cd: "To be added", fnxl: "To be added", fsrl: "To be added" },
  "Gemini 2.5 Flash": { ner: "To be added", finer_ord: "To be added", finred: "To be added", sc: "To be added", cd: "To be added", fnxl: "To be added", fsrl: "To be added" },
  "Grok4": { ner: "To be added", finer_ord: "To be added", finred: "To be added", sc: "To be added", cd: "To be added", fnxl: "To be added", fsrl: "To be added" },
  "Claude 4 Sonnet": { ner: "To be added", finer_ord: "To be added", finred: "To be added", sc: "To be added", cd: "To be added", fnxl: "To be added", fsrl: "To be added" },
  "Llama-3.1-8B-Instruct": { ner: "To be added", finer_ord: "To be added", finred: "To be added", sc: "To be added", cd: "To be added", fnxl: "To be added", fsrl: "To be added" },
  "DeepSeek Chat": { ner: "To be added", finer_ord: "To be added", finred: "To be added", sc: "To be added", cd: "To be added", fnxl: "To be added", fsrl: "To be added" },
};

// 计算每个模型的平均分 (用于排序和显示)
const calculateAverage = (scores) => {
  const values = Object.values(scores).filter(v => typeof v === 'number');
  return values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : 0;
};


// Calculate min/max averages
export const useAverageRange = (data) => {
  return useMemo(() => {
    if (!data || data.length === 0) {
      return {
        minAverage: 0,
        maxAverage: 100,
      };
    }
    const averages = data.map((item) => item.model.average_score);
    return {
      minAverage: Math.min(...averages),
      maxAverage: Math.max(...averages),
    };
  }, [data]);
};

// Generate colors for scores
export const useColorGenerator = (minAverage, maxAverage) => {
  return useMemo(() => {
    const colorCache = new Map();
    return (value) => {
      const cached = colorCache.get(value);
      if (cached) return cached;

      const normalizedValue = (value - minAverage) / (maxAverage - minAverage);
      const red = Math.round(255 * (1 - normalizedValue) * 1);
      const green = Math.round(255 * normalizedValue) * 1;
      // const color = `rgba(${red}, ${green}, 0, 1)`;
      const color = `rgba(${red}, 0, ${green}, 1)`;
      colorCache.set(value, color);
      return color;
    };
  }, [minAverage, maxAverage]);
};

// Process data with boolean standardization
export const useProcessedData = (data, averageMode, visibleColumns) => {
  return useMemo(() => {
    // 使用 Information Extraction Leaderboard 数据创建模型列表
    const modelList = [];

    // 遍历所有模型
    Object.entries(LEADERBOARD_SCORES).forEach(([modelName, scores], index) => {
      // 计算平均分
      const averageScore = calculateAverage(scores);

      // 创建模型数据
      modelList.push({
        id: `model-${index}`,
        model: {
          name: modelName,
          average_score: averageScore,
          type: "chat",
        },
        evaluations: {
          ...scores,
        },
        features: {
          is_moe: false,
          is_flagged: false,
          is_highlighted_by_maintainer: false,
          is_merged: false,
          is_not_available_on_hub: false,
        },
        metadata: {
          submission_date: new Date().toISOString(),
        },
        isMissing: false,
      });
    });

    // 根据平均分排序
    modelList.sort((a, b) => {
      if (a.model.average_score === null && b.model.average_score === null)
        return 0;
      if (a.model.average_score === null) return 1;
      if (b.model.average_score === null) return -1;
      return b.model.average_score - a.model.average_score;
    });

    // 添加排名
    return modelList.map((item, index) => ({
      ...item,
      static_rank: index + 1,
    }));
  }, [data, averageMode, visibleColumns]);
};


// Common filtering logic
export const useFilteredData = (
  processedData,
  selectedPrecisions,
  selectedTypes,
  paramsRange,
  searchValue,
  selectedBooleanFilters,
  rankingMode,
  pinnedModels = [],
  isOfficialProviderActive = false
) => {
  return useMemo(() => {
    // 由于使用的是硬编码数据，这里直接返回所有数据而不进行过滤
    return processedData.map((item, index) => ({
      ...item,
      dynamic_rank: index + 1,
      rank: rankingMode === "static" ? item.static_rank : index + 1,
      isPinned: pinnedModels.includes(item.id),
    }));
  }, [
    processedData,
    rankingMode,
    pinnedModels,
  ]);
};

// Column visibility management
export const useColumnVisibility = (visibleColumns = []) => {
  // Create secure visibility object
  const columnVisibility = useMemo(() => {
    // Check visible columns
    const safeVisibleColumns = Array.isArray(visibleColumns)
      ? visibleColumns
      : [];

    const visibility = {};
    try {
      safeVisibleColumns.forEach((columnKey) => {
        if (typeof columnKey === "string") {
          visibility[columnKey] = true;
        }
      });
    } catch (error) {
      console.warn("Error in useColumnVisibility:", error);
    }
    return visibility;
  }, [visibleColumns]);

  return columnVisibility;
};
